/**
 * 見かけの大きさ(拡大倍率)のリアルタイム調整UI。
 * ×1 = 視角補正済みの「肉眼と同じ見かけの大きさ」。
 * 実物では小さすぎて見づらい場合にスライダーで最大×20まで拡大できる。
 * 倍率はlocalStorageに史跡ごとに永続化する。
 */
export class ScaleUI {
  private root: HTMLElement;
  private toggleBtn: HTMLButtonElement;
  private storageKey: string;

  constructor(
    container: HTMLElement,
    toolbar: HTMLElement,
    siteId: string,
    screenAngleDeg: number,
    compensation: number,
    onChange: (userMult: number) => void,
  ) {
    this.storageKey = `shiseki-ar:visual-scale:${siteId}`;
    // デフォルト×5: 実物と同じ(×1)では初見の体験者が機影を見失いやすいため
    const saved = Math.min(20, Math.max(1, Number(localStorage.getItem(this.storageKey) ?? '5') || 5));

    this.root = document.createElement('div');
    this.root.className = 'calibration';
    this.root.style.display = 'none';
    this.root.innerHTML = `
      <div class="hint">
        機体と爆弾の見かけの大きさを調整します。×1は肉眼で見たときと同じ視角です
        (画面視角${screenAngleDeg.toFixed(0)}°・補正×${compensation.toFixed(1)}を適用済み)。
        実物は高高度でとても小さく見えるため、見づらい場合は拡大してください。
        ※拡大すると遠近感の錯覚で「低く・近く」感じられますが、
        飛行高度9,100m・速度・針路は一切変わっていません(大きさのみ変化)。
      </div>
      <div class="row">
        <input type="range" min="1" max="20" step="0.5" value="${saved}" />
        <div class="value">×${fmt(saved)}</div>
      </div>
      <div class="actions">
        <button class="btn-small" data-act="reset">実物(×1)</button>
        <button class="btn-small accent" data-act="close">完了</button>
      </div>
    `;

    const slider = this.root.querySelector('input') as HTMLInputElement;
    const valueEl = this.root.querySelector('.value') as HTMLElement;

    slider.addEventListener('input', () => {
      const v = Number(slider.value);
      valueEl.textContent = `×${fmt(v)}`;
      localStorage.setItem(this.storageKey, String(v));
      onChange(v);
    });
    (this.root.querySelector('[data-act="reset"]') as HTMLElement).addEventListener('click', () => {
      slider.value = '1';
      slider.dispatchEvent(new Event('input'));
    });
    (this.root.querySelector('[data-act="close"]') as HTMLElement).addEventListener('click', () => {
      this.root.style.display = 'none';
    });

    this.toggleBtn = document.createElement('button');
    this.toggleBtn.className = 'btn-small';
    this.toggleBtn.textContent = '🔍 大きさ調整';
    this.toggleBtn.addEventListener('click', () => {
      this.root.style.display = this.root.style.display === 'none' ? 'flex' : 'none';
    });

    toolbar.appendChild(this.toggleBtn);
    container.appendChild(this.root);
  }

  get savedMult(): number {
    return Math.min(20, Math.max(1, Number(localStorage.getItem(this.storageKey) ?? '5') || 5));
  }
}

function fmt(v: number): string {
  return v.toFixed(1).replace(/\.0$/, '');
}
